import { useCustomValuePreviewActions } from "PFApp/components/custom_value_preview/use_custom_value_preview";
import { LoadingDots } from "PFComponents/loading_dots";
import SidePanel from "PFComponents/side_panel/side_panel";
import { isRankable } from "PFCore/helpers/custom_type";
import { ownProfileCustomTypesAccessLevels } from "PFCore/helpers/custom_types";
import { useCustomValue } from "PFCore/hooks/queries/custom_fields/use_custom_value";
import { useProfileCustomValue } from "PFCore/hooks/queries/custom_fields/use_profile_custom_value";
import { useCustomValuesCreate } from "PFCore/hooks/queries/custom_values/use_custom_values_create";
import { useDictionaryConnectionCreate } from "PFCore/hooks/queries/custom_values/use_dictionary_connection_create";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { useUnbiasedProfileName } from "PFCore/hooks/use_unbiased_profile_name";
import { AccessLevel, Experience } from "PFTypes";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useCustomValuePreviewContext } from "../context/custom_value_preview_context";
import css from "../custom_value_preview.module.scss";
import { useProfileName } from "../hooks/use_profile_name";
import { CustomValueSelect } from "../parts/custom_value_select";
import { ProfileCustomValueFooter } from "../parts/footer/profile_custom_value_footer";
import { ProfileCustomValueContent } from "./parts/profile_custom_value_content";

export const ProfileCustomValuePreview = () => {
  const { t } = useTranslation("translation", { keyPrefix: "customValueSidepanel" });
  const { data: currentProfile } = useCurrentProfile();
  const { hidePreview } = useCustomValuePreviewActions();
  const { getUnbiasedProfileName } = useUnbiasedProfileName();

  const {
    show,
    customType,
    allowSearch,
    profileId,
    canEdit,
    isNew,
    isClosing,
    customValueId,
    setSelectedCustomValueId,
    isUnbiasedMode,
    index
  } = useCustomValuePreviewContext();

  const { profileName, isFetchingProfileName } = useProfileName();

  const { data: customValue, isFetching: isFetchingCustomValue } = useCustomValue(customValueId!, {
    enabled: Boolean(show && customValueId && !isClosing)
  });

  const connectionQueryEnabled = Boolean(show && customValueId && !isNew && !isClosing);
  // TODO: [SP-2554] useProfileCustomValue should be replaced with new endpoint for profile connection only
  const { data: profileConnectionData, isFetching: isFetchingProfileConnection } = useProfileCustomValue(
    customValueId!,
    profileId!,
    {
      enabled: connectionQueryEnabled
    }
  );

  const profileConnection = connectionQueryEnabled ? profileConnectionData : undefined;

  const { mutateAsync: createCustomValue, isPending: isCreatingCustomValue } = useCustomValuesCreate();
  const { mutateAsync: createDictionaryConnection, isPending: isUpdating } = useDictionaryConnectionCreate();

  const [experience, setExperience] = useState<Experience | null>(null);
  const [isCore, setIsCore] = useState(false);
  const [isDevelopmental, setIsDevelopmental] = useState(false);

  useEffect(() => {
    setExperience(profileConnection?.experience ?? null);
    setIsCore(profileConnection?.top ?? false);
    setIsDevelopmental(profileConnection?.developmental ?? false);
  }, [profileConnection]);

  const onResetForm = useCallback(() => {
    setSelectedCustomValueId(null);
    setExperience(null);
    setIsCore(false);
    setIsDevelopmental(false);
  }, [setSelectedCustomValueId]);

  useEffect(() => {
    if (allowSearch) {
      onResetForm();
    }
  }, [allowSearch, onResetForm]);

  const rankable = isRankable(customType);
  const isLoading =
    isFetchingProfileName || isFetchingCustomValue || isFetchingProfileConnection || isCreatingCustomValue;

  const isAllDataFetched =
    (isUnbiasedMode || !!profileName) && !!customValue && (isNew || !!profileConnection);

  const isMe = profileId === currentProfile.id;

  const title = isNew && !customValue ? t("addCustomValue") : customValue?.text;
  const subtitle =
    isMe && canEdit
      ? customType?.display_as
      : isUnbiasedMode
        ? getUnbiasedProfileName({ id: Number(profileId) }, true)
        : profileName;

  const showSubtitle = !!title && (!isNew || !!customValue);
  const showCustomValueSelect = !isLoading && isNew && !customValue && allowSearch;
  const showContent = !isLoading && isAllDataFetched;

  const allowEdit =
    !!canEdit &&
    isMe &&
    ownProfileCustomTypesAccessLevels(currentProfile)?.[customType.id] === AccessLevel.ReadWrite;

  const hasCoreDevelopmentalAttribute = customType.name === "skills";
  const hasAnyFormField = allowEdit && (rankable || hasCoreDevelopmentalAttribute);
  const isFormFilled = !rankable || experience !== null;

  const renderFooter = useCallback(
    ({ onSidePanelClose }) => (
      <ProfileCustomValueFooter
        customValue={customValue}
        experience={experience}
        isCore={isCore}
        isDevelopmental={isDevelopmental}
        allowEdit={allowEdit}
        isRemoveDisabled={isLoading}
        isSaveDisabled={isLoading || !customValue || !isFormFilled}
        isSaveTooltipVisible={!!customValue && !isFormFilled}
        showSaveButton={hasAnyFormField}
        createDictionaryConnection={createDictionaryConnection}
        onClose={onSidePanelClose}
      />
    ),
    [
      allowEdit,
      createDictionaryConnection,
      customValue,
      experience,
      isCore,
      isDevelopmental,
      isFormFilled,
      isLoading,
      hasAnyFormField
    ]
  );

  return (
    <SidePanel
      fullHeight
      show={show}
      title={title}
      subTitle={showSubtitle ? subtitle : undefined}
      isSidePanelClosing={isClosing}
      footerRenderer={renderFooter}
      onClose={hidePreview}
      zIndex={index}
    >
      {!!isUpdating && (
        <div className={css.savingOverlay}>
          <LoadingDots circlesEnabled circleSize="xl" />
        </div>
      )}
      <div className={css.sidePanelRoot}>
        {isLoading && <LoadingDots centered circlesEnabled circleSize="sm" />}
        {showCustomValueSelect && (
          <CustomValueSelect customType={customType} createCustomValue={createCustomValue} />
        )}
        {showContent && (
          <ProfileCustomValueContent
            experience={experience}
            isCore={isCore}
            isDevelopmental={isDevelopmental}
            customValue={customValue}
            profileConnection={profileConnection}
            allowEdit={allowEdit}
            hasAnyFormField={hasAnyFormField}
            onExperienceChange={setExperience}
            onCoreChange={setIsCore}
            onDevelopmentalChange={setIsDevelopmental}
            onResetForm={onResetForm}
          />
        )}
      </div>
    </SidePanel>
  );
};
